import { uniq } from "lodash";
import StyledInput from "notes-and-forms-v2/components/styled-input/StyledInput";
import getClasses from "utils/getClasses";
import {
  ElementViewForClipboardProps,
  ElementViewProps,
} from "../AtomicElementView";
import styles from "./RadiosGridContent.module.scss";

type RadiosGridData = Record<string, string>;

function parseRawData(data: any, element: RadiosGridElement): RadiosGridData {
  if (!data || typeof data !== "object") {
    return {};
  }

  const mappedEntries = Object.entries(data).map(([key, value]) => {
    // The answers are stored in the following format, to maintain compatibility with v1 components:
    // ``` [row.title]: column.title ```
    // However, GradientJ currently responds with:
    // ``` [row.id]: column.title ```
    // The conversion below will also allow for ``` [row.id]: column.id ``` in case we encounter it,
    // as well as remove any invalid fields

    const row = element.rows.find((row) => row.title === key || row.id === key);
    const column = element.columns.find(
      (column) => column.title === value || column.id === value,
    );

    if (!row || !column) {
      return [];
    }

    return [row?.title, column?.title];
  });

  const validMappedEntries = mappedEntries.filter((entry) => entry.length);

  return Object.fromEntries(validMappedEntries);
}

export function RadiosGridContentForClipboard({
  element,
  data: rawData,
}: ElementViewForClipboardProps<RadiosGridElement>) {
  const data = parseRawData(rawData, element);
  return (
    <ul>
      {element.rows.map((row) => (
        <li key={row.id}>
          {row.title}: {data[row.title] || "No answer"}
        </li>
      ))}
    </ul>
  );
}

function getFilteredColumns(
  element: RadiosGridElement,
  options: TemplateContentOptions,
  data: RadiosGridData,
) {
  if (!options.hideUnselectedOptionsForRadios) {
    return element.columns;
  }

  const optionsSelectedLeastOnce = uniq(Object.values(data));
  return element.columns.filter((column) =>
    optionsSelectedLeastOnce.includes(column.title),
  );
}

export default function RadiosGridContent({
  data: rawData,
  element,
  onChange,
  isLocked,
  isPrintMode,
  isDownloadMode,
  disabled,
  showSuicidalWarningInPhq9,
  options = {},
  showAsMissing,
}: ElementViewProps<RadiosGridElement>) {
  const data = parseRawData(rawData, element);

  const filteredColumns = getFilteredColumns(element, options, data);

  return (
    <div className={styles.container}>
      <div
        className={styles["answers-grid"]}
        style={{
          gridTemplateColumns: `2fr repeat(${filteredColumns.length},1fr)`,
        }}
      >
        <div
          className={getClasses(
            styles["grid-row-wrapper"],
            isPrintMode && styles.print,
            "template-solid-bg",
          )}
          style={{ background: "red" }}
        >
          <div className={styles["top-left-cell"]}>{/* Empty cell */}</div>
          {filteredColumns.map((column) => (
            <div key={column.id}>{column.title}</div>
          ))}
        </div>

        {element.rows.map((row, i) => (
          <div
            key={row.id}
            className={getClasses(
              styles["grid-row-wrapper"],
              isPrintMode && styles.print,
              showSuicidalWarningInPhq9 && i === 8 && styles.warningRow,
              showAsMissing && !data[row.title] && styles.missing,
            )}
          >
            <div className={styles["grid-row-title"]}>{row.title}</div>
            {filteredColumns.map((column) => (
              <label key={column.id} htmlFor={[row.id, column.id].join("-")}>
                <StyledInput
                  id={[row.id, column.id].join("-")}
                  type="radio"
                  checked={data[row.title] === column.title}
                  onChange={() =>
                    onChange({
                      ...data,
                      [row.title]: column.title,
                    })
                  }
                  disabled={disabled || isLocked}
                  disableStyling={isDownloadMode}
                />
              </label>
            ))}
          </div>
        ))}
      </div>

      {!isDownloadMode && !options?.disableStickyRowHeaderForGrids && (
        <div
          className={getClasses(styles["answers-grid"], styles["grid-copy"])}
          style={{
            gridTemplateColumns: `2fr repeat(${filteredColumns.length},1fr)`,
          }}
        >
          <div
            className={getClasses(
              styles["grid-row-wrapper"],
              isPrintMode && styles.print,
              "template-solid-bg",
            )}
          >
            <div className={styles["top-left-cell"]}>{/* Empty cell */}</div>
            {filteredColumns.map((column) => (
              <div key={column.id}>{column.title}</div>
            ))}
          </div>

          {element.rows.map((row, i) => (
            <div
              key={row.id}
              className={getClasses(
                styles["grid-row-wrapper"],
                isPrintMode && styles.print,
                showSuicidalWarningInPhq9 && i === 8 && styles.warningRow,
                showAsMissing && !data[row.title] && styles.missing,
              )}
            >
              <div className={styles["grid-row-title"]}>{row.title}</div>
              {filteredColumns.map((column) => (
                <label key={column.id}>
                  <StyledInput type="radio" disabled={true} />
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
